import { useState, useCallback } from 'react'
import { Upload, X, Loader2, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

interface LogoUploadProps {
  value: string | null | undefined
  onChange: (url: string | null) => void
  accountId: string
  disabled?: boolean
}

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']

export function LogoUpload({
  value,
  onChange,
  accountId,
  disabled = false,
}: LogoUploadProps) {
  const { toast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const uploadLogo = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast({ title: 'Error', description: 'Invalid file type. Please use PNG, JPG, WebP, or SVG.', variant: 'destructive' })
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({ title: 'Error', description: 'File too large. Maximum size is 2MB.', variant: 'destructive' })
        return
      }

      setIsUploading(true)

      try {
        // Delete old logo if exists
        if (value) {
          try {
            const url = new URL(value)
            const pathParts = url.pathname.split('/storage/v1/object/public/account-logos/')
            if (pathParts[1]) {
              await supabase.storage.from('account-logos').remove([pathParts[1]])
            }
          } catch {
            // Ignore errors when removing old logo
          }
        }

        const fileExt = file.name.split('.').pop()
        const fileName = `${accountId}/logo.${fileExt}`

        const { error: uploadError } = await supabase.storage
          .from('account-logos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: true,
          })

        if (uploadError) throw uploadError

        const {
          data: { publicUrl },
        } = supabase.storage.from('account-logos').getPublicUrl(fileName)

        // Add cache busting parameter
        const urlWithCacheBust = `${publicUrl}?t=${Date.now()}`
        onChange(urlWithCacheBust)
        toast({ title: 'Logo uploaded successfully' })
      } catch (error) {
        console.error('Upload error:', error)
        toast({ title: 'Error', description: 'Failed to upload logo', variant: 'destructive' })
      } finally {
        setIsUploading(false)
      }
    },
    [accountId, onChange, value, toast]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        uploadLogo(file)
      }
    },
    [uploadLogo]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files?.[0]
      if (file) {
        uploadLogo(file)
      }
    },
    [uploadLogo]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const removeLogo = useCallback(async () => {
    if (!value) return

    try {
      const url = new URL(value)
      const pathParts = url.pathname.split('/storage/v1/object/public/account-logos/')
      if (pathParts[1]) {
        // Remove cache busting parameter from path
        const cleanPath = pathParts[1].split('?')[0]
        await supabase.storage.from('account-logos').remove([cleanPath])
      }
    } catch (error) {
      console.error('Error removing logo:', error)
    }

    onChange(null)
    toast({ title: 'Logo removed' })
  }, [value, onChange, toast])

  if (value) {
    return (
      <div className="relative w-fit">
        <div className="relative h-32 w-32 overflow-hidden rounded-lg border bg-muted">
          <img
            src={value}
            alt="Business Logo"
            className="h-full w-full object-contain p-2"
          />
        </div>
        <Button
          type="button"
          variant="destructive"
          size="icon"
          className="absolute -right-2 -top-2 h-6 w-6"
          onClick={removeLogo}
          disabled={disabled}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors',
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/25 hover:border-muted-foreground/50',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
      {isUploading ? (
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      ) : (
        <>
          {isDragging ? (
            <Upload className="h-8 w-8 text-primary" />
          ) : (
            <Building2 className="h-8 w-8 text-muted-foreground" />
          )}
          <p className="mt-1 text-xs text-muted-foreground text-center px-2">
            {isDragging ? 'Drop logo' : 'Upload logo'}
          </p>
        </>
      )}
    </div>
  )
}
